import {
    styled,
    Box,
    Button,
    Chip,
    FormControlLabel,
    Switch,
    Typography,
    Alert,
    Collapse,
    Divider,
    Skeleton,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    Stack,
} from '@mui/material';
import {
    Check, Code, ContentCopy, ExpandLess, ExpandMore,
} from '@mui/icons-material';
import { CopyButton } from 'src/components/common/CopyButton';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import {
    useEffect, useState, useRef,
} from 'react';
import { TitledAppBar } from 'src/components/common/TitledAppBar';
import { DocumentSourceInputs } from 'src/components/MatchingVisualizer/DocumentSourceInput';
import { LoadResetButtons } from 'src/components/MatchingVisualizer/LoadResetButtons';
import { useMatchingVisualizerLoad } from 'src/hooks/useMatchingVisualizerLoad';
import { useQueryCompositeMatching } from 'src/hooks/useQueryCompositeMatching';
import { buildCompositeMatchingUrl } from 'src/services/DesignConductorService';
import { useDocumentSource } from 'src/hooks/useDocumentSource';
import { TargetProductOptions } from 'src/components/MatchingVisualizer/TargetProductOptions';
import { JsonViewer } from 'src/components/MatchingVisualizer/JsonViewer';
import { AssetsImages, DpcImage } from 'src/components/MatchingVisualizer/AssetsImages';
import { LoadingStateImage } from 'src/components/MatchingVisualizer/LoadingStateImage';
import { DestinationProductOptions } from 'src/components/MatchingVisualizer/DestinationProductOptions';
import { PreviewUrlBuilder } from 'src/utils/PreviewUrlBuilder';
import { normalizePreviewUrl, stripSceneFromPreviewUrl } from 'src/utils/previewSceneUtils';
import {
    CompositeMatchEntry, CompositeMatchingParams, CompositeMatchingType, RawPersonalizationContext,
} from 'src/types/compositeMatching';
import { MATCHING_PRESETS, MatchingPreset } from 'src/data/matchingPresets';
import { GhostSvg } from 'src/components/common/icons/GhostSvg';

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(8)}`,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    height: `calc(100vh - ${theme.spacing(20)})`,
}));

const ContentWrapper = styled(Box)(() => ({
    width: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
}));

const TwoColumnLayout = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
}));

const LeftColumn = styled(Box)(({ theme }) => ({
    flex: `0 0 calc(40% - ${theme.spacing(2)})`,
    minHeight: 0,
    display: 'flex',
    [theme.breakpoints.down('md')]: {
        flex: '1 1 auto',
    },
}));

const RightColumn = styled(Box)(({ theme }) => ({
    flex: `0 0 calc(60% - ${theme.spacing(2)})`,
    minWidth: 0,
    overflow: 'auto',
    [theme.breakpoints.down('md')]: {
        flex: '1 1 auto',
    },
}));

const PlaceholderCard = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const MATCHER_LABELS: Record<CompositeMatchingType, string> = {
    Template: 'Template Matching',
    Resizer: 'Template Matching (Resized)',
    Logo: 'Logo Matching',
    Photo: 'Photo Matching',
    AGGenerate: 'Composition Matching',
};

const getMatcherLabel = (matchingType: CompositeMatchingType): string => MATCHER_LABELS[matchingType] ?? matchingType;

const getMatcherCardTitle = (entry: CompositeMatchEntry): string => {
    const matchTypeLabel = getMatcherLabel(entry.matchingType);

    return entry.matchingStrategy ? `${matchTypeLabel}-${entry.matchingStrategy}` : matchTypeLabel;
};

const getMatcherEntryKey = (entry: CompositeMatchEntry, index: number): string => `${entry.matchingType}:${entry.matchingStrategy ?? ''}:${index}`;

type CopyActionButtonProps = {
    label: string;
    textToCopy: string;
    disabled?: boolean;
};

const CopyActionButton = ({ label, textToCopy, disabled }: CopyActionButtonProps): JSX.Element => {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!copied) return undefined;
        const handle = setTimeout(() => setCopied(false), 3000);

        return (): void => clearTimeout(handle);
    }, [copied]);

    return (
        <Button
            color={copied ? 'success' : 'primary'}
            disabled={disabled}
            size="small"
            startIcon={copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
            variant="outlined"
            onClick={(): void => {
                if (!textToCopy) return;
                navigator.clipboard
                    .writeText(textToCopy)
                    .then(() => setCopied(true))
                    .catch(() => undefined);
            }}
        >
            {label}
        </Button>
    );
};

export const MatchingVisualizerPage = (): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const [debugDialogOpen, setDebugDialogOpen] = useState(false);
    const [sandboxEnabled, setSandboxEnabled] = useState<boolean>(false);
    const [sendAuthHeader, setSendAuthHeader] = useState<boolean>(false);
    const [productKey, setProductKey] = useState<string>('');
    const [version, setVersion] = useState<number | undefined>(undefined);
    const leftPanelRef = useRef<HTMLDivElement>(null);
    const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
    const [culture, setCulture] = useState<string>('');
    const [uri, setUri] = useState('');
    const [pdsResult, setPdsResult] = useState('');
    const [showPdsResult, setShowPdsResult] = useState(false);
    const [dpcId, setDpcId] = useState('');
    const [showDpcId, setShowDpcId] = useState(false);
    const [designConceptIdInput, setDesignConceptIdInput] = useState('');
    const [showDesignConceptId, setShowDesignConceptId] = useState(false);
    const [brandId, setBrandId] = useState('');
    const [showBrandId, setShowBrandId] = useState(false);
    const [sourceProductKey, setSourceProductKey] = useState('');
    const [sourceProductVersion, setSourceProductVersion] = useState<number | undefined>(undefined);
    const [sourceProductOptions, setSourceProductOptions] = useState<Record<string, string>>({});
    const [showSourceProduct, setShowSourceProduct] = useState(false);
    const [scenePurpose, setScenePurpose] = useState('');
    const [showScenePurpose, setShowScenePurpose] = useState(false);
    const [agGeneratePriority, setAgGeneratePriority] = useState('');
    const [showAgGeneratePriority, setShowAgGeneratePriority] = useState(false);
    const [expandedOptions, setExpandedOptions] = useState<Record<string, boolean>>({});
    const [expandedNoScene, setExpandedNoScene] = useState<Record<string, boolean>>({});
    const [selectedPresetIndex, setSelectedPresetIndex] = useState<number | ''>('');
    const [loadParams, setLoadParams] = useState<{
        documentSourceUri: string;
        productKey: string;
        productVersion: number;
        culture: string;
        fullProductSpecification: Record<string, string>;
        pdsResultUri?: string;
        dpcId?: string;
        designConceptId?: string;
        brandId?: string;
        sourceProductKey?: string;
        sourceProductVersion?: number;
        sourceProductOptions?: Record<string, string>;
        scenePurpose?: string;
        agGeneratePriority?: string;
        sandbox?: boolean;
        sendAuthHeader?: boolean;
    } | null>(null);

    // Shared document source query
    const documentSourceQuery = useDocumentSource(uri);

    // Only call matching hook if document source is successful
    const matchingResults = useMatchingVisualizerLoad(
        loadParams && !documentSourceQuery.error ? loadParams : null,
        documentSourceQuery,
    );

    const compositeDocumentUrl = loadParams?.documentSourceUri || undefined; const compositeDpcId = loadParams?.dpcId || undefined;
    const compositeDesignConceptId = loadParams?.designConceptId || undefined;
    const compositeBrandId = loadParams?.brandId || undefined;
    const hasCompositeSource = !!compositeDocumentUrl
        || !!compositeDpcId
        || !!compositeDesignConceptId
        || !!compositeBrandId;
    const compositeParams: CompositeMatchingParams | null = loadParams && hasCompositeSource
        ? {
            productKey: loadParams.productKey,
            productVersion: loadParams.productVersion,
            culture: loadParams.culture,
            productOptions: loadParams.fullProductSpecification,
            documentUrl: compositeDocumentUrl,
            designPersonalizationContextId: compositeDpcId,
            designConceptId: compositeDesignConceptId,
            brandId: compositeBrandId,
            sourceProductKey: loadParams.sourceProductKey || undefined,
            sourceProductVersion: loadParams.sourceProductVersion,
            sourceProductOptions: loadParams.sourceProductOptions,
            scenePurpose: loadParams.scenePurpose || undefined,
            agGeneratePriority: loadParams.agGeneratePriority || undefined,
            sandbox: loadParams.sandbox ?? false,
            sendAuthHeader: loadParams.sendAuthHeader ?? false,
        }
        : null;
    const compositeMatchingData = useQueryCompositeMatching(compositeParams);

    const loadedSandboxEnabled = loadParams?.sandbox ?? false;
    const loadedSendAuthHeader = loadParams?.sendAuthHeader ?? false;

    const { url: compositeUrl, pathWithQuery: compositePathWithQuery } = compositeParams
        ? buildCompositeMatchingUrl(compositeParams)
        : { url: null, pathWithQuery: null };

    const bearerForCurl = typeof accessToken === 'string' ? accessToken : '<bearer-token>';

    let curlSnippet = '';

    if (compositeUrl) {
        curlSnippet = loadedSendAuthHeader
            ? `curl -X GET '${compositeUrl}' \\\n  -H 'Authorization: Bearer ${bearerForCurl}'`
            : `curl -X GET '${compositeUrl}'`;
    }

    const compositeMatches = compositeMatchingData.data?.matches ?? [];
    const hasSiteResultPreview = !!matchingResults.pdsResultPreview.previewUrl;
    const winnerIndex = compositeMatches.findIndex((m) => m.match !== null);

    const rawPersonalizationContextRaw = compositeMatchingData.data?.debugState?.rawPersonalizationContext;
    const rawPersonalizationContext = ((): RawPersonalizationContext | string | undefined => {
        if (!rawPersonalizationContextRaw) return undefined;
        if (typeof rawPersonalizationContextRaw !== 'string') return rawPersonalizationContextRaw;
        try {
            return JSON.parse(rawPersonalizationContextRaw) as RawPersonalizationContext;
        } catch {
            return rawPersonalizationContextRaw;
        }
    })();
    const dpcImages: DpcImage[] = typeof rawPersonalizationContext === 'object'
        ? (rawPersonalizationContext?.designPersonalizationData?.images ?? []).map((entry) => ({
            previewUrl: entry.image?.previewUrl || undefined,
            originalSourceUrl: entry.image?.originalSourceUrl || undefined,
            purpose: entry.purpose,
        }))
        : [];

    const isStaticDesign = documentSourceQuery.data?.templateIdentifier?.type === 'TEMPLATE_TOKEN';
    const templateToken = isStaticDesign
        ? (documentSourceQuery.data?.templateIdentifier?.value ?? undefined)
        : undefined;
    const originalSourceTemplateUrl = templateToken
        ? PreviewUrlBuilder.build(templateToken, '400')
        : undefined;

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const documentUri = params.get('documentUri');
        const productKeyParam = params.get('productKey');
        const versionParam = params.get('productVersion');
        const cultureParam = params.get('culture');
        const optionsParam = params.get('options');
        const pdsResultParam = params.get('siteResult');
        const dpcIdParam = params.get('dpcId');
        const designConceptIdParam = params.get('designConceptId');
        const brandIdParam = params.get('brandId');
        const sourceProductKeyParam = params.get('sourceProductKey');
        const sourceProductVersionParam = params.get('sourceProductVersion');
        const sourceProductOptionsParam = params.get('sourceProductOptions');
        const scenePurposeParam = params.get('scenePurpose');
        const agGeneratePriorityParam = params.get('agGeneratePriority');
        const sandboxParam = params.get('sandbox') === 'true';
        const sendAuthHeaderParam = params.get('sendAuthHeader') === 'true';

        setSandboxEnabled(sandboxParam);
        setSendAuthHeader(sendAuthHeaderParam);

        if (documentUri) {
            setUri(decodeURIComponent(documentUri));
        }
        if (productKeyParam) {
            setProductKey(decodeURIComponent(productKeyParam));
        }
        if (versionParam) {
            setVersion(Number(versionParam));
        }
        if (cultureParam) {
            setCulture(decodeURIComponent(cultureParam));
        }
        if (pdsResultParam) {
            setPdsResult(decodeURIComponent(pdsResultParam));
            setShowPdsResult(true);
        }
        if (dpcIdParam) {
            setDpcId(decodeURIComponent(dpcIdParam));
            setShowDpcId(true);
        }
        if (designConceptIdParam) {
            setDesignConceptIdInput(decodeURIComponent(designConceptIdParam));
            setShowDesignConceptId(true);
        }
        if (brandIdParam) {
            setBrandId(decodeURIComponent(brandIdParam));
            setShowBrandId(true);
        }
        if (sourceProductKeyParam) {
            setSourceProductKey(decodeURIComponent(sourceProductKeyParam));
            setShowSourceProduct(true);
        }
        if (sourceProductVersionParam) {
            setSourceProductVersion(Number(sourceProductVersionParam));
            setShowSourceProduct(true);
        }
        if (sourceProductOptionsParam) {
            try {
                const parsed = JSON.parse(decodeURIComponent(sourceProductOptionsParam));

                if (parsed && typeof parsed === 'object') {
                    setSourceProductOptions(parsed);
                    setShowSourceProduct(true);
                }
            } catch {
                setSourceProductOptions({});
            }
        }
        if (scenePurposeParam) {
            setScenePurpose(decodeURIComponent(scenePurposeParam));
            setShowScenePurpose(true);
        }
        if (agGeneratePriorityParam) {
            setAgGeneratePriority(decodeURIComponent(agGeneratePriorityParam));
            setShowAgGeneratePriority(true);
        }

        if (optionsParam) {
            try {
                const decoded = decodeURIComponent(optionsParam);
                const parsed = JSON.parse(decoded);

                if (parsed && typeof parsed === 'object') {
                    setSelectedOptions(parsed);
                }
            } catch {
                setSelectedOptions({});
            }
        }

        const hasSourceParam = !!(documentUri || dpcIdParam || designConceptIdParam || brandIdParam);

        if (hasSourceParam && productKeyParam && versionParam && cultureParam) {
            let parsedOptions: Record<string, string> = {};

            if (optionsParam) {
                try {
                    parsedOptions = JSON.parse(decodeURIComponent(optionsParam)) as Record<string, string>;
                } catch {
                    parsedOptions = {};
                }
            }

            setLoadParams({
                documentSourceUri: documentUri ? decodeURIComponent(documentUri) : '',
                productKey: decodeURIComponent(productKeyParam),
                productVersion: Number(versionParam),
                culture: decodeURIComponent(cultureParam),
                fullProductSpecification: parsedOptions,
                pdsResultUri: pdsResultParam ? decodeURIComponent(pdsResultParam) : undefined,
                dpcId: dpcIdParam ? decodeURIComponent(dpcIdParam) : undefined,
                designConceptId: designConceptIdParam ? decodeURIComponent(designConceptIdParam) : undefined,
                brandId: brandIdParam ? decodeURIComponent(brandIdParam) : undefined,
                sourceProductKey: sourceProductKeyParam ? decodeURIComponent(sourceProductKeyParam) : undefined,
                sourceProductVersion: sourceProductVersionParam ? Number(sourceProductVersionParam) : undefined,
                sourceProductOptions: sourceProductOptionsParam
                    ? ((): Record<string, string> | undefined => {
                        try { return JSON.parse(decodeURIComponent(sourceProductOptionsParam)); } catch { return undefined; }
                    })()
                    : undefined,
                scenePurpose: scenePurposeParam ? decodeURIComponent(scenePurposeParam) : undefined,
                agGeneratePriority: agGeneratePriorityParam ? decodeURIComponent(agGeneratePriorityParam) : undefined,
                sandbox: sandboxParam,
                sendAuthHeader: sendAuthHeaderParam,
            });
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();

        if (uri) {
            params.set('documentUri', encodeURIComponent(uri));
        }
        if (productKey) {
            params.set('productKey', encodeURIComponent(productKey));
        }
        if (version !== undefined) {
            params.set('productVersion', String(version));
        }
        if (culture) {
            params.set('culture', encodeURIComponent(culture));
        }
        if (Object.keys(selectedOptions).length > 0) {
            params.set('options', encodeURIComponent(JSON.stringify(selectedOptions)));
        }
        if (pdsResult) {
            params.set('siteResult', encodeURIComponent(pdsResult));
        }
        if (dpcId) {
            params.set('dpcId', encodeURIComponent(dpcId));
        }
        if (designConceptIdInput) {
            params.set('designConceptId', encodeURIComponent(designConceptIdInput));
        }
        if (brandId) {
            params.set('brandId', encodeURIComponent(brandId));
        }
        if (sourceProductKey) {
            params.set('sourceProductKey', encodeURIComponent(sourceProductKey));
        }
        if (sourceProductVersion !== undefined) {
            params.set('sourceProductVersion', String(sourceProductVersion));
        }
        if (Object.keys(sourceProductOptions).length > 0) {
            params.set('sourceProductOptions', encodeURIComponent(JSON.stringify(sourceProductOptions)));
        }
        if (scenePurpose) {
            params.set('scenePurpose', encodeURIComponent(scenePurpose));
        }
        if (agGeneratePriority) {
            params.set('agGeneratePriority', encodeURIComponent(agGeneratePriority));
        }
        if (sandboxEnabled) {
            params.set('sandbox', 'true');
        } else {
            params.delete('sandbox');
        }
        if (sendAuthHeader) {
            params.set('sendAuthHeader', 'true');
        } else {
            params.delete('sendAuthHeader');
        }

        const newUrl = `${window.location.pathname}?${params.toString()}`;

        window.history.replaceState({}, '', newUrl);
    }, [uri, productKey, version, culture, selectedOptions, pdsResult, dpcId, designConceptIdInput, brandId, sourceProductKey, sourceProductVersion, sourceProductOptions, scenePurpose, agGeneratePriority, sandboxEnabled, sendAuthHeader]);

    const handleLoad = (documentUri: string): void => {
        if (!productKey || !version) {
            return;
        }

        const nextParams = {
            documentSourceUri: documentUri,
            productKey,
            productVersion: version,
            culture,
            fullProductSpecification: selectedOptions,
            pdsResultUri: pdsResult || undefined,
            dpcId: dpcId || undefined,
            designConceptId: designConceptIdInput || undefined,
            brandId: brandId || undefined,
            sourceProductKey: sourceProductKey || undefined,
            sourceProductVersion,
            sourceProductOptions: Object.keys(sourceProductOptions).length > 0 ? sourceProductOptions : undefined,
            scenePurpose: scenePurpose || undefined,
            agGeneratePriority: agGeneratePriority || undefined,
            sandbox: sandboxEnabled,
            sendAuthHeader,
        };

        setExpandedOptions({});
        setExpandedNoScene({});
        setLoadParams(null);
        queueMicrotask(() => setLoadParams(nextParams));
    };

    const handleReset = (): void => {
        setUri('');
        setProductKey('');
        setVersion(undefined);
        setSelectedOptions({});
        setCulture('');
        setPdsResult('');
        setShowPdsResult(false);
        setDpcId('');
        setShowDpcId(false);
        setDesignConceptIdInput('');
        setShowDesignConceptId(false);
        setBrandId('');
        setShowBrandId(false);
        setSourceProductKey('');
        setSourceProductVersion(undefined);
        setSourceProductOptions({});
        setShowSourceProduct(false);
        setScenePurpose('');
        setShowScenePurpose(false);
        setAgGeneratePriority('');
        setShowAgGeneratePriority(false);
        setExpandedOptions({});
        setExpandedNoScene({});
        setSelectedPresetIndex('');
        setLoadParams(null);
    };

    const handlePresetSelect = (index: number | ''): void => {
        if (index === '') return;

        const scrollTop = leftPanelRef.current?.scrollTop ?? 0;

        requestAnimationFrame(() => {
            if (leftPanelRef.current) {
                leftPanelRef.current.scrollTop = scrollTop;
            }
        });

        const preset: MatchingPreset = MATCHING_PRESETS[index];

        const presetProductOptions = preset.productOptions ?? {};

        setUri(preset.documentSourceUri);
        setProductKey(preset.productKey);
        setVersion(preset.productVersion);
        setCulture(preset.culture);
        setSelectedOptions(presetProductOptions);

        if (preset.siteResultUri) {
            setPdsResult(preset.siteResultUri);
            setShowPdsResult(true);
        } else {
            setPdsResult('');
            setShowPdsResult(false);
        }

        if (preset.dpcId) {
            setDpcId(preset.dpcId);
            setShowDpcId(true);
        } else {
            setDpcId('');
            setShowDpcId(false);
        }

        setDesignConceptIdInput('');
        setShowDesignConceptId(false);
        setBrandId('');
        setShowBrandId(false);
        setScenePurpose('');
        setShowScenePurpose(false);
        setAgGeneratePriority('');
        setShowAgGeneratePriority(false);

        const presetSourceProductKey = preset.sourceProductKey ?? '';
        const presetSourceProductVersion = preset.sourceProductVersion;
        const presetSourceProductOptions = preset.sourceProductOptions ?? {};
        const hasSourceProduct = !!preset.sourceProductKey
            || preset.sourceProductVersion !== undefined
            || Object.keys(presetSourceProductOptions).length > 0;

        setSourceProductKey(presetSourceProductKey);
        setSourceProductVersion(presetSourceProductVersion);
        setSourceProductOptions(presetSourceProductOptions);
        setShowSourceProduct(hasSourceProduct);

        setSelectedPresetIndex(index);
        setExpandedOptions({});
        setExpandedNoScene({});
        setLoadParams(null);
        queueMicrotask(() => setLoadParams({
            documentSourceUri: preset.documentSourceUri,
            productKey: preset.productKey,
            productVersion: preset.productVersion,
            culture: preset.culture,
            fullProductSpecification: presetProductOptions,
            pdsResultUri: preset.siteResultUri,
            dpcId: preset.dpcId,
            sourceProductKey: preset.sourceProductKey,
            sourceProductVersion: preset.sourceProductVersion,
            sourceProductOptions: Object.keys(presetSourceProductOptions).length > 0 ? presetSourceProductOptions : undefined,
            sandbox: sandboxEnabled,
            sendAuthHeader,
        }));
    };

    const clearPreset = (): void => setSelectedPresetIndex('');

    return (
        <>
            <TitledAppBar title="Matching Visualizer" />
            <StyledContainer>
                <ContentWrapper>
                    <TwoColumnLayout>
                        <LeftColumn>
                            <PlaceholderCard sx={{
                                flexDirection: 'column', alignItems: 'stretch', height: '100%', overflow: 'hidden',
                            }}
                            >
                                <Box
                                    ref={leftPanelRef}
                                    sx={{
                                        flex: 1,
                                        overflow: 'auto',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Box sx={{ mb: 2 }}>
                                        <Box alignItems="flex-start" display="flex" gap={2} justifyContent="space-between" sx={{ mb: 1 }}>
                                            <Typography fontWeight={600} variant="h6">
                                                Inputs
                                            </Typography>
                                            <Box alignItems="center" display="flex" gap={1}>
                                                <Tooltip title="When enabled, your Authorization bearer token is sent.">
                                                    <FormControlLabel
                                                        control={(
                                                            <Switch
                                                                checked={sendAuthHeader}
                                                                color="warning"
                                                                size="small"
                                                                onChange={(_e, checked): void => setSendAuthHeader(checked)}
                                                            />
                                                        )}
                                                        label="Send Auth Header"
                                                        labelPlacement="start"
                                                        sx={{ m: 0 }}
                                                    />
                                                </Tooltip>
                                                <Tooltip title="Swap environments. When enabled, read requests and preview links target the test environment.">
                                                    <FormControlLabel
                                                        control={(
                                                            <Switch
                                                                checked={sandboxEnabled}
                                                                color="warning"
                                                                size="small"
                                                                onChange={(_e, checked): void => setSandboxEnabled(checked)}
                                                            />
                                                        )}
                                                        label="Test Environment"
                                                        labelPlacement="start"
                                                        sx={{ m: 0 }}
                                                    />
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                        <Typography color="text.secondary" variant="body2">
                                            Configure your matching inputs and target product, then click Load.
                                        </Typography>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Load Preset"
                                        size="small"
                                        sx={{ mb: 2 }}
                                        value={selectedPresetIndex}
                                        onChange={(e): void => handlePresetSelect(e.target.value as unknown as number | '')}
                                    >
                                        {MATCHING_PRESETS.map((preset, index) => (
                                            <MenuItem key={preset.label} value={index}>
                                                {`${index + 1}. ${preset.label}`}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <DocumentSourceInputs
                                        agGeneratePriority={agGeneratePriority}
                                        brandId={brandId}
                                        designConceptId={designConceptIdInput}
                                        documentSourceQuery={documentSourceQuery}
                                        documentUri={uri}
                                        dpcId={dpcId}
                                        pdsResult={pdsResult}
                                        scenePurpose={scenePurpose}
                                        showAgGeneratePriority={showAgGeneratePriority}
                                        showBrandId={showBrandId}
                                        showDesignConceptId={showDesignConceptId}
                                        showDpcId={showDpcId}
                                        showPdsResult={showPdsResult}
                                        showScenePurpose={showScenePurpose}
                                        showSourceProduct={showSourceProduct}
                                        sourceProductKey={sourceProductKey}
                                        sourceProductOptions={sourceProductOptions}
                                        sourceProductVersion={sourceProductVersion}
                                        onAgGeneratePriorityChange={(val: string): void => { setAgGeneratePriority(val); clearPreset(); }}
                                        onBrandIdChange={(val: string): void => { setBrandId(val); clearPreset(); }}
                                        onDesignConceptIdChange={(val: string): void => { setDesignConceptIdInput(val); clearPreset(); }}
                                        onDocumentUriChange={(val: string): void => { setUri(val); clearPreset(); }}
                                        onDpcIdChange={(val: string): void => { setDpcId(val); clearPreset(); }}
                                        onPdsResultChange={(val: string): void => { setPdsResult(val); clearPreset(); }}
                                        onScenePurposeChange={(val: string): void => { setScenePurpose(val); clearPreset(); }}
                                        onShowAgGeneratePriorityChange={(show: boolean): void => setShowAgGeneratePriority(show)}
                                        onShowBrandIdChange={(show: boolean): void => setShowBrandId(show)}
                                        onShowDesignConceptIdChange={(show: boolean): void => setShowDesignConceptId(show)}
                                        onShowDpcIdChange={(show: boolean): void => setShowDpcId(show)}
                                        onShowPdsResultChange={(show: boolean): void => setShowPdsResult(show)}
                                        onShowScenePurposeChange={(show: boolean): void => setShowScenePurpose(show)}
                                        onShowSourceProductChange={(show: boolean): void => setShowSourceProduct(show)}
                                        onSourceProductKeyChange={(val: string): void => setSourceProductKey(val)}
                                        onSourceProductOptionsChange={(opts: Record<string, string>): void => setSourceProductOptions(opts)}
                                        onSourceProductVersionChange={(val: number | undefined): void => setSourceProductVersion(val)}
                                    />
                                    <TargetProductOptions
                                        culture={culture}
                                        initialOptions={selectedOptions}
                                        key={`preset-${selectedPresetIndex}`}
                                        productKey={productKey}
                                        version={version}
                                        onChangeCulture={(c): void => setCulture(c)}
                                        onChangeOptions={(opts): void => setSelectedOptions(opts)}
                                        onChangeProductKey={(k): void => setProductKey(k)}
                                        onChangeVersion={(v): void => setVersion(v)}
                                    />
                                </Box>
                                <Box sx={{ flexShrink: 0, pt: 2 }}>
                                    <LoadResetButtons
                                        brandId={brandId}
                                        culture={culture}
                                        designConceptId={designConceptIdInput}
                                        dpcId={dpcId}
                                        hasAnyInput={
                                            !!uri
                                            || !!dpcId
                                            || !!designConceptIdInput
                                            || !!brandId
                                            || !!sourceProductKey
                                            || sourceProductVersion !== undefined
                                            || Object.keys(sourceProductOptions).length > 0
                                            || !!pdsResult
                                            || !!productKey
                                            || version !== undefined
                                            || !!culture
                                            || Object.keys(selectedOptions).length > 0
                                            || selectedPresetIndex !== ''
                                            || loadParams !== null
                                        }
                                        isLoading={compositeMatchingData.isInitialLoading}
                                        productKey={productKey}
                                        productVersion={version}
                                        uri={uri}
                                        onLoad={handleLoad}
                                        onReset={handleReset}
                                    />
                                </Box>
                            </PlaceholderCard>
                        </LeftColumn>
                        <RightColumn>
                            <PlaceholderCard sx={{
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                justifyContent: 'stretch',
                                gap: 2,
                                minHeight: '100%',
                            }}
                            >
                                <Box sx={{
                                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1,
                                }}
                                >
                                    <Box>
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', gap: 1, mb: 1,
                                        }}
                                        >
                                            <Typography fontWeight={600} variant="h6">
                                                Composite Matching Results
                                            </Typography>
                                            {loadedSandboxEnabled && (
                                            <Chip
                                                color="warning"
                                                label="Test environment"
                                                size="small"
                                                variant="filled"
                                            />
                                            )}
                                        </Box>
                                        <Typography color="text.secondary" variant="body2">
                                            The composite matcher&apos;s selected match is highlighted. Matchers run in priority order (P1 → P4). Each one shows its result or the reason no match was found.
                                        </Typography>
                                    </Box>

                                    <Tooltip title={compositeUrl ? 'View composite matching request + debug response' : 'Load matching to enable'}>
                                        <span>
                                            <IconButton
                                                aria-label="View composite matching request and debug response"
                                                disabled={!compositeUrl}
                                                size="small"
                                                sx={{ p: 0.5, color: 'text.secondary' }}
                                                onClick={(): void => setDebugDialogOpen(true)}
                                            >
                                                <Code sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                                {documentSourceQuery.error && (
                                    <Box sx={{ width: '100%' }}>
                                        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                                            {`Document Source Error: ${documentSourceQuery.error.message}`}
                                        </Alert>
                                    </Box>
                                )}
                                {compositeMatchingData.isInitialLoading && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Box>
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center', gap: 2, mb: 1,
                                            }}
                                            >
                                                <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body2">
                                                    Composite Matcher:
                                                </Typography>
                                                <Skeleton animation="wave" height={40} variant="rectangular" width={240} />
                                            </Box>
                                            <Divider sx={{ mt: 1, mb: 2 }} />
                                            <Box>
                                                <Box sx={{ mb: 2 }}>
                                                    <Skeleton
                                                        animation="wave"
                                                        height={36.5}
                                                        sx={{ borderRadius: 1 }}
                                                        variant="rectangular"
                                                        width="100%"
                                                    />
                                                </Box>
                                                <Typography gutterBottom variant="h6">
                                                    Destination Preview
                                                </Typography>
                                                <Box sx={{ textAlign: 'center' }}>
                                                    <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                                                        <Skeleton
                                                            animation="wave"
                                                            height={400}
                                                            sx={{ borderRadius: 1 }}
                                                            variant="rectangular"
                                                            width={500}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                            <Box sx={{ mt: 3 }}>
                                                <Typography gutterBottom variant="h6">
                                                    Destination Product Options
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {[1, 2, 3, 4].map((i) => (
                                                        <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                                                            <Skeleton animation="wave" height={40} width="40%" />
                                                            <Skeleton animation="wave" height={40} width="50%" />
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        <Box>
                                            <Typography gutterBottom variant="h6">
                                                DocumentSource Preview
                                            </Typography>
                                            <Skeleton
                                                animation="wave"
                                                sx={{
                                                    width: '100%',
                                                    height: 300,
                                                    borderRadius: 1,
                                                }}
                                                variant="rectangular"
                                            />
                                        </Box>
                                        <Box>
                                            <Skeleton animation="wave" height={32} sx={{ mb: 1 }} width="60%" />
                                            <Skeleton
                                                animation="wave"
                                                sx={{
                                                    width: '100%',
                                                    height: 150,
                                                    borderRadius: 1,
                                                }}
                                                variant="rectangular"
                                            />
                                        </Box>
                                    </Box>
                                )}
                                {compositeMatchingData.error && (
                                    <Box sx={{ width: '100%' }}>
                                        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                                            {`Composite Matching: ${compositeMatchingData.error.message}.`}
                                        </Alert>
                                    </Box>
                                )}
                                {!compositeMatchingData.isInitialLoading && !loadParams && (
                                    <Box sx={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 4,
                                    }}
                                    >
                                        <Box sx={{
                                            width: 120,
                                            filter: 'grayscale(1)',
                                            opacity: 0.6,
                                            '& svg': { width: '100%', height: 'auto', display: 'block' },
                                        }}
                                        >
                                            <GhostSvg />
                                        </Box>
                                        <Typography color="text.secondary" textAlign="center" variant="body2">
                                            Click Load to see matching results
                                        </Typography>
                                    </Box>
                                )}
                                {loadParams && !documentSourceQuery.error && !compositeMatchingData.isInitialLoading
                                  && (
                                  <Box sx={{
                                      display: 'flex', flexDirection: 'column', gap: 2, width: '100%',
                                  }}
                                  >
                                      {compositeMatchingData.data && (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                          {compositeMatches.map((entry, index) => {
                                              const available = entry.match !== null;
                                              const entryKey = getMatcherEntryKey(entry, index);
                                              const isWinner = index === winnerIndex;
                                              const isExpanded = !!expandedOptions[entryKey];
                                              const isNoSceneExpanded = !!expandedNoScene[entryKey];
                                              const matchPreviewUrl = entry.match?.colorVariants[0]?.previewUrl;
                                              const noScenePreviewUrl = matchPreviewUrl
                                                  ? stripSceneFromPreviewUrl(matchPreviewUrl)
                                                  : undefined;

                                              return (
                                                  <Box
                                                      key={entryKey}
                                                      sx={{
                                                          border: '1px solid',
                                                          borderColor: isWinner ? 'primary.main' : 'divider',
                                                          borderRadius: 2,
                                                          p: 2,
                                                          backgroundColor: isWinner ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                                                      }}
                                                  >
                                                      <Box sx={{
                                                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1,
                                                      }}
                                                      >
                                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                              <Typography color="text.secondary" component="span" sx={{ minWidth: 24 }} variant="caption">
                                                                  {`P${index + 1}`}
                                                              </Typography>
                                                              <Typography sx={{ fontWeight: 500 }} variant="subtitle1">
                                                                  {getMatcherCardTitle(entry)}
                                                              </Typography>
                                                          </Box>
                                                          {isWinner && (
                                                              <Chip
                                                                  label="Selected Matcher"
                                                                  size="small"
                                                                  sx={{
                                                                      height: 22,
                                                                      fontSize: '0.7rem',
                                                                      backgroundColor: 'primary.main',
                                                                      color: 'white',
                                                                  }}
                                                              />
                                                          )}
                                                          {available && !isWinner && (
                                                              <Chip
                                                                  label="Available"
                                                                  size="small"
                                                                  sx={{
                                                                      height: 22,
                                                                      fontSize: '0.7rem',
                                                                      borderColor: 'primary.main',
                                                                      color: 'primary.main',
                                                                  }}
                                                                  variant="outlined"
                                                              />
                                                          )}
                                                          {!available && (
                                                              <Chip
                                                                  label="Unavailable"
                                                                  size="small"
                                                                  sx={{
                                                                      height: 22,
                                                                      fontSize: '0.7rem',
                                                                      borderColor: 'error.main',
                                                                      color: 'error.main',
                                                                  }}
                                                                  variant="outlined"
                                                              />
                                                          )}
                                                      </Box>
                                                      {!available && (
                                                          <Alert icon={false} severity="error">
                                                              <strong>Match failure reason: </strong>
                                                              {entry.details}
                                                          </Alert>
                                                      )}
                                                      {available && entry.match && (
                                                          <>
                                                              <Box sx={{ textAlign: 'center', mb: 1 }}>
                                                                  <LoadingStateImage
                                                                      fullWidth
                                                                      imageSx={{
                                                                          width: '100%',
                                                                          maxHeight: '400px',
                                                                          objectFit: 'contain',
                                                                      }}
                                                                      src={entry.match.colorVariants[0]?.previewUrl
                                                                          ? normalizePreviewUrl(entry.match.colorVariants[0].previewUrl)
                                                                          : undefined}
                                                                  />
                                                              </Box>
                                                              <Box sx={{ display: 'flex', gap: 1 }}>
                                                                  {noScenePreviewUrl && (
                                                                      <Button
                                                                          color="secondary"
                                                                          endIcon={isNoSceneExpanded ? <ExpandLess /> : <ExpandMore />}
                                                                          size="small"
                                                                          variant="text"
                                                                          onClick={(): void => setExpandedNoScene((prev) => ({
                                                                              ...prev,
                                                                              [entryKey]: !prev[entryKey],
                                                                          }))}
                                                                      >
                                                                          No-scene Preview
                                                                      </Button>
                                                                  )}
                                                                  <Button
                                                                      color="secondary"
                                                                      endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                                                                      size="small"
                                                                      variant="text"
                                                                      onClick={(): void => setExpandedOptions((prev) => ({
                                                                          ...prev,
                                                                          [entryKey]: !prev[entryKey],
                                                                      }))}
                                                                  >
                                                                      Product Options
                                                                  </Button>
                                                              </Box>
                                                              {noScenePreviewUrl && (
                                                                  <Collapse mountOnEnter in={isNoSceneExpanded}>
                                                                      <Box sx={{ textAlign: 'center', mt: 1 }}>
                                                                          <LoadingStateImage
                                                                              fullWidth
                                                                              imageSx={{
                                                                                  width: '100%',
                                                                                  maxHeight: '400px',
                                                                                  objectFit: 'contain',
                                                                              }}
                                                                              src={noScenePreviewUrl}
                                                                          />
                                                                      </Box>
                                                                  </Collapse>
                                                              )}
                                                              <Collapse in={isExpanded}>
                                                                  <Box sx={{ mt: 1 }}>
                                                                      <DestinationProductOptions
                                                                          destinationOptions={entry.match.completeProductOptions}
                                                                          inputOptions={selectedOptions}
                                                                      />
                                                                  </Box>
                                                              </Collapse>
                                                          </>
                                                      )}
                                                  </Box>
                                              );
                                          })}
                                      </Box>
                                      )}
                                      {hasSiteResultPreview && (
                                          <Box>
                                              <Typography gutterBottom variant="h6">
                                                  Site Result Preview
                                              </Typography>
                                              <LoadingStateImage
                                                  imageSx={{
                                                      maxHeight: '400px',
                                                      maxWidth: '100%',
                                                      width: 'auto',
                                                      objectFit: 'contain',
                                                  }}
                                                  src={matchingResults.pdsResultPreview.previewUrl ?? undefined}
                                              />
                                          </Box>
                                      )}

                                      {compositeMatches.some((m) => m.match !== null) && (
                                          <Divider sx={{ my: 2 }} />
                                      )}

                                      {matchingResults.documentSourcePreviewUrl && (
                                      <Box>
                                          <Typography gutterBottom variant="h6">
                                              Document Source Preview
                                          </Typography>
                                          <LoadingStateImage
                                              imageSx={{
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 1,
                                                  maxHeight: '400px',
                                                  maxWidth: '100%',
                                                  width: 'auto',
                                                  objectFit: 'contain',
                                              }}
                                              src={matchingResults.documentSourcePreviewUrl}
                                          />
                                      </Box>
                                      )}
                                      {isStaticDesign && originalSourceTemplateUrl && (
                                      <Box>
                                          <Typography gutterBottom variant="h6">
                                              Original Source Template Preview
                                          </Typography>
                                          <LoadingStateImage
                                              imageSx={{
                                                  border: '1px solid',
                                                  borderColor: 'divider',
                                                  borderRadius: 1,
                                                  maxHeight: '400px',
                                                  maxWidth: '100%',
                                                  width: 'auto',
                                                  objectFit: 'contain',
                                              }}
                                              src={originalSourceTemplateUrl}
                                          />
                                      </Box>
                                      )}
                                      {rawPersonalizationContext && (
                                      <JsonViewer
                                          data={rawPersonalizationContext}
                                          title="Design Personalization Context (from design conductor)"
                                      />
                                      )}
                                      {rawPersonalizationContext && (
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                          <Typography gutterBottom variant="h6">
                                              Extracted Images / Logos
                                          </Typography>
                                          <AssetsImages images={dpcImages} />
                                      </Box>
                                      )}
                                  </Box>
                                  )}
                            </PlaceholderCard>
                        </RightColumn>
                    </TwoColumnLayout>
                </ContentWrapper>
            </StyledContainer>
            <Dialog
                fullWidth
                maxWidth="md"
                open={debugDialogOpen}
                onClose={(): void => setDebugDialogOpen(false)}
            >
                <DialogTitle>Composite matching request (debug)</DialogTitle>
                <DialogContent dividers>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2 }}>
                        <CopyActionButton
                            disabled={!compositeUrl}
                            label="Copy full URL"
                            textToCopy={compositeUrl ?? ''}
                        />
                        <CopyActionButton
                            disabled={!compositeUrl}
                            label="Copy path + query"
                            textToCopy={compositePathWithQuery ? `/${compositePathWithQuery}` : ''}
                        />
                        <CopyActionButton
                            disabled={!compositeUrl}
                            label={sendAuthHeader ? 'Copy as cURL (with bearer)' : 'Copy as cURL'}
                            textToCopy={curlSnippet}
                        />
                    </Stack>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1,
                    }}
                    >
                        <Typography sx={{ fontWeight: 500 }} variant="subtitle2">
                            Design conductor response
                        </Typography>
                        <CopyButton
                            size="small"
                            textToCopy={JSON.stringify(compositeMatchingData.data ?? null, null, 2)}
                            tooltipTitle="Copy the full design conductor response"
                        />
                    </Box>
                    <Box
                        component="pre"
                        sx={{
                            backgroundColor: 'grey.100',
                            padding: 2,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            maxHeight: '60vh',
                            overflowX: 'auto',
                            overflowY: 'auto',
                            whiteSpace: 'pre',
                            width: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box',
                            m: 0,
                        }}
                    >
                        {compositeMatchingData.data
                            ? JSON.stringify(compositeMatchingData.data, null, 2)
                            : '(no response yet)'}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};
